import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Stack,
  TextField,
  Alert,
  Chip,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import { MuiFileInput } from "mui-file-input";
import getBanners from "../../api/banner/getBanners";
import uploadBanner from "../../api/banner/uploadBanners";
import setTags from "../../api/store/setTags";
import getTags from "../../api/store/getTags";

const WebPage = () => {
  const [banners, setBanners] = useState([{ image: null, link: "", preview: "" }]);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tags, setTagsState] = useState([]);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await getBanners();
        if (data && Array.isArray(data.Image) && Array.isArray(data.Link)) {
          const combinedData = data.Image.map((image, index) => ({
            image,
            link: data.Link[index] || "",
            preview: image,
          }));
          setBanners(
            combinedData.length > 0
              ? combinedData
              : [{ image: null, link: "", preview: "" }]
          );
        }
      } catch (err) {
        setError("Failed to fetch banners: " + err.message);
      }
    };

    fetchBanners();
    const fetchTags = async () => {
      try {
        const data = await getTags();

        setTagsState(data.tags); // Adjust this if data structure differs
      } catch (err) {
        setError("Failed to load tags: " + err.message);
      }
    };

    fetchTags();
  }, []);

  const handleImgChange = (file, index) => {
    const newBanners = [...banners];
    if (file) {
      newBanners[index] = { ...newBanners[index], image: file, preview: URL.createObjectURL(file) };
    } else {
      newBanners[index] = { ...newBanners[index], image: null, preview: "" };
    }
    setBanners(newBanners);

    if (file && index === banners.length - 1 && banners.length < 4) {
      setBanners([...newBanners, { image: null, link: "", preview: "" }]);
    }
  };

  const handleLinkChange = (e, index) => {
    const newBanners = [...banners];
    newBanners[index] = { ...newBanners[index], link: e.target.value };
    setBanners(newBanners);
  };

  const handleUploadClicked = async () => {
    try {
      const bannersData = await Promise.all(
        banners.map(async (banner) => ({
          image: banner.image && banner.image instanceof File ? await convertToBase64(banner.image) : banner.image,
          link: banner.link,
        }))
      );

      const filteredBannersData = bannersData.filter(banner => banner.image !== null);

      await uploadBanner({
        Image: filteredBannersData.map(banner => banner.image),
        Link: filteredBannersData.map(banner => banner.link)
      });
      setIsSuccess(true);
    } catch (err) {
      setIsSuccess(false);
      setError(err.message);
    }
  };

  const handleRemoveBanner = (index) => {
    const newBanners = banners.filter((_, i) => i !== index);
    setBanners(
      newBanners.length > 0
        ? newBanners
        : [{ image: null, link: "", preview: "" }]
    );
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSetTags = async () => {
    try {
      if (!tags.includes(inputValue.trim())) {
        setTagsState([...tags, inputValue.trim()]);
        setInputValue("");
      }
      const response = await setTags([...tags, inputValue.trim()]);
      setIsSuccess(true);
      console.log("Tags updated successfully:", response);
    } catch (err) {
      setIsSuccess(false);
      setError("Failed to set tags: " + err.message);
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTagsState((prevTags) => prevTags.filter((tag) => tag !== tagToDelete));
  };

  const handleAddTag = (event) => {
    if (event.key === "Enter" && event.target.value.trim() !== "") {
      if (!tags.includes(event.target.value.trim())) {
        setTagsState([...tags, event.target.value.trim()]);
        setInputValue("");
      }
    }
  };

  return (
    <div>
      <Card sx={{ margin: 1 }}>
        <CardContent>
          <Stack spacing={2} direction={"column"}  alignItems="left">
            <Typography fontSize="large">網頁橫幅圖片</Typography>
            {banners.map((banner, index) => (
              <Stack key={index} direction={"row"}  alignItems="center" spacing={2}>
              <Stack key={index} direction={"column"} alignItems="center" spacing={1}>
                <MuiFileInput
                  title="網頁橫幅圖片"
                  placeholder="選擇圖片"
                  color="info"
                  inputProps={{ accept: "image/jpeg" }}
                  onChange={(fileValue) => handleImgChange(fileValue, index)}
                  size="small"
                  value={banner.image || null}
                />
                <TextField
                  label="輸入網址"
                  variant="outlined"
                  value={banner.link}
                  fullWidth
                  onChange={(e) => handleLinkChange(e, index)}
                  size="small"
                /></Stack>
                {banner.preview && (
                  <img src={banner.preview} alt={`Preview ${index}`} style={{ width: "120px", height: "120px" }} />
                )}
                {index >= 0 && (
                  <Button variant="contained" color="error" onClick={() => handleRemoveBanner(index)}>
                    移除
                  </Button>
                )}
              </Stack>
            ))}
            {banners.length < 4 && banners[banners.length - 1].image && (
              <MuiFileInput
                title="網頁橫幅圖片"
                placeholder="選擇圖片"
                color="info"
                inputProps={{ accept: "image/jpeg" }}
                onChange={(fileValue) => handleImgChange(fileValue, banners.length)}
                sx={{maxWidth:"50%"}}
                size="small"
              />
            )}
            <Button variant="contained" sx={{maxWidth:"10%"}} color="info" onClick={handleUploadClicked}>
              上傳
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Card sx={{ margin: 1 }}>
        <CardContent>
          <Stack direction="column" alignItems={"flex-start"} spacing={3}>
            <Box flexDirection={"row"} maxWidth={"100%"}>
              {tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  color="secondary"
                />
              ))}
            </Box>
            <Stack direction={"row"} spacing={1} height={"5vh"} alignItems={"center"}>
              <TextField
                label="輸入標籤"
                variant="outlined"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleAddTag}
              />
              <Button variant="contained" color="info" onClick={handleSetTags}>
                新增標籤
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {isSuccess && <Alert severity="success">操作成功！</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
    </div>
  );
};

export default WebPage;
